import React, { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import Axios from "axios";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { DateTime } from "luxon";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import { ColorModeContext, tokens } from "../theme";

function GroupParentForm() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);
  const { pid } = useParams();
  const [scheduleData, setScheduleData] = useState(null);
  const [error, setError] = useState(null);
  const [openAttendModal, setOpenAttendModal] = useState(false);
  const [openNotAttendModal, setOpenNotAttendModal] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState(false);
  const [timeError, setTimeError] = useState("");

  useEffect(() => {
    const fetchScheduleData = async () => {
      try {
        //get student uuid from query params
        const student_uuid = new URLSearchParams(window.location.search).get('student_uuid');
        const response = await Axios.get(`${process.env.REACT_APP_URL}/api/v1/group-makeup/parent/${pid}?student_uuid=${student_uuid}`);
        if (response.status === 200) {
          if (response.data.is_private_course) {
            setError("1:1 수업은 해당 페이지에서 확인할 수 없습니다.");
            return;
          }
          setScheduleData(response.data);
        }
        else {
          if (response.data.error === "You have already confirmed the makeup!") {
            setOpenNotAttendModal(false);
            setShowSuccessScreen(true);
          }
          else {
            setError(response.data.error);
          }
        }
      } catch (err) {
        if (err.response?.data?.message === "You have already confirmed the makeup!") {
          setOpenNotAttendModal(false);
          setShowSuccessScreen(true);
        }
        else{
          setError("일정 데이터를 불러올수 없습니다. 다시 시도해주세요.");
        }
      }
    };

    if (pid) {
      fetchScheduleData();
    }
  }, [pid]);

  // Move this useEffect to before any early returns
  useEffect(() => {
    // Only run if scheduleData is available
    if (scheduleData) {
      const start = DateTime.fromISO(scheduleData.final_window.start, { zone: "America/New_York" });
      const now = DateTime.now().setZone("America/New_York");
      const isSameDayOrPast = now.startOf('day') >= start.startOf('day');
      if (isSameDayOrPast) {
        setTimeError("이미 시작된 일정이거나 당일에는 참석/불참을 선택할 수 없습니다.");
      } else {
        setTimeError("");
      }
    }
  }, [scheduleData]);

  if (error) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        width: '100%'
      }}>
        <Typography 
          color="error" 
          sx={{ 
            textAlign: 'center',
            width: '100%',
            maxWidth: '600px',
            px: 2
          }}
        >
          {error}
        </Typography>
      </Box>
    );
  }
  const SuccessScreen = () => (
    <Box sx={{
      p: 3,
      minHeight: "100vh",
      maxWidth: "1200px",
      margin: "0 auto",
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center'
    }}>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        mb: 4,
        width: '100%'
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          pb: 2,
          borderBottom: `1px solid ${colors.grey[800]}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="/imgs/aone-logo.png"
              alt="AONE Logo"
              style={{
                height: 40,
                width: 'auto'
              }}
            />
          </Box>
        </Box>
      </Box>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        py: 8,
        px: 4,
        maxWidth: 600,
        width: '100%'
      }}>
        <CheckCircleIcon
          sx={{
            fontSize: 80,
            color: colors.greenAccent[500],
            mb: 3
          }}
        />
        <Typography
          variant="h2"
          sx={{
            mb: 3,
            color: colors.primary[100],
            fontWeight: 700,
            fontSize: { xs: '1.8rem', md: '2.5rem' },
            lineHeight: 1.2
          }}
        >
          응답이 제출되었습니다.
        </Typography>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          flexDirection: 'column',
          maxWidth: 500
        }}>
          <Typography
            sx={{
              color: colors.grey[300],
              fontSize: '1rem',
              lineHeight: 1.5
            }}
          >
            감사합니다!
          </Typography>
        </Box>
      </Box>
    </Box>
  );
  if (showSuccessScreen) {
    return <SuccessScreen />;
  }

  if (!scheduleData ) {
    return <Typography sx={{ textAlign: 'center', mt: 4 }}>Loading schedule data...</Typography>;
  }

  const { course_name, og_course_date, final_window } = scheduleData;
  const start = DateTime.fromISO(final_window.start, { zone: "America/New_York" });
  const end = DateTime.fromISO(final_window.end, { zone: "America/New_York" });

  // NEW: Calculate if today is the same day or after the scheduled start
  const now = DateTime.now().setZone("America/New_York");
  const isSameDayOrPast = now.startOf('day') >= start.startOf('day');

  // Show error automatically if buttons should not be shown
  // useEffect(() => {
  //   if (isSameDayOrPast) {
  //     setTimeError("이미 시작된 일정이거나 당일에는 참석/불참을 선택할 수 없습니다.");
  //   } else {
  //     setTimeError("");
  //   }
  // }, [isSameDayOrPast]);

  const handleAttend = async () => {
    try {
      const student_uuid = new URLSearchParams(window.location.search).get('student_uuid');
      const newEvent_id = new URLSearchParams(window.location.search).get('newEvent_id');
      const response = await Axios.post(`${process.env.REACT_APP_URL}/api/v1/group-makeup/parent/${pid}`, {
        student_uuid: student_uuid,
        will_attend: true,
        newEvent_id: newEvent_id
      });
      if (response.status === 200) {
        setOpenAttendModal(false);
        setShowSuccessScreen(true);
      }
      else {
        setError(response.data.error);
      }
    }catch(err) {
      setError(err.response?.data?.error || "Something went wrong");
    }
  };

  const handleNotAttend = async () => {
    try {
      const student_uuid = new URLSearchParams(window.location.search).get('student_uuid');
      const newEvent_id = new URLSearchParams(window.location.search).get('newEvent_id');
      const response = await Axios.post(`${process.env.REACT_APP_URL}/api/v1/group-makeup/parent/${pid}`, {
        student_uuid: student_uuid,
        will_attend: false,
        newEvent_id: newEvent_id
      });
      if (response.status === 200) {
        setOpenNotAttendModal(false);
        setShowSuccessScreen(true);
      }
      else {
        setError(response.data.error);
      }
    }catch(err) {
      setError(err.response?.data?.error || "Something went wrong");
    }
  };


  return (
    <Box sx={{ p: 3, minHeight: "100vh", maxWidth: "1200px", margin: "0 auto" }}>
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        mb: 4
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          pb: 2,
          borderBottom: `1px solid ${colors.grey[800]}`
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="/imgs/aone-logo.png"
              alt="AONE Logo"
              style={{
                height: 40,
                width: 'auto'
              }}
            />
          </Box>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1
          }}>
            <IconButton
              onClick={colorMode.toggleColorMode}
              size="small"
              sx={{
                backgroundColor: colors.primary[600],
                color: colors.primary[100],
                '&:hover': {
                  backgroundColor: colors.primary[600],
                  color: colors.primary[100],
                },
              }}
            >
              {theme.palette.mode === "dark" ? <DarkModeOutlinedIcon /> : <LightModeOutlinedIcon />}
            </IconButton>
            <Box sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: colors.greenAccent[500]
            }} />
            <Typography sx={{
              color: colors.grey[300],
              fontSize: '0.9rem'
            }}>
              EST Timezone
            </Typography>
          </Box>
        </Box>
        <Box sx={{
          textAlign: 'center',
          py: 4
        }}>
          <Typography
            variant="h2"
            sx={{
              mb: 1,
              color: colors.primary[100],
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.5rem' }
            }}
          >
            보강 수업 일정 안내
          </Typography>
          <Typography
            variant="h4"
            sx={{
              mb: 3,
              color: colors.grey[300],
              fontWeight: 500,
              fontSize: { xs: '1.2rem', md: '1.4rem' }
            }}
          >
            <strong>{course_name}</strong> • <strong>{DateTime.fromISO(og_course_date).toFormat('MMMM d, yyyy')}</strong>
          </Typography>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            flexDirection: 'column'
          }}>
            <Typography
              sx={{
                color: colors.grey[300],
                fontSize: '1rem'
              }}
            >
              아래 일정을 확인하시고 참석 여부를 선택해주세요.
            </Typography>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1,
              borderRadius: 1,
              bgcolor: '#FFF3E0'
            }}>
              <Box sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: '#ED6C02'
              }} />
              <Typography sx={{
                color: '#B76E00',
                fontSize: '0.9rem'
              }}>
                아래 일정은 미국 동부 시간(EST) 기준입니다.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        mb: 6
      }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 3,
          width: '100%',
          maxWidth: 800
        }}>
          <Box sx={{
            flex: 1,
            minHeight: 200,
            border: `1px solid ${colors.grey[700]}`,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            bgcolor: colors.primary[600]
          }}>
            <Typography
              variant="h4"
              sx={{
                mb: 1,
                color: colors.grey[300],
                fontWeight: 700
              }}
            >
              {start.toFormat('MMMM d, yyyy')}
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                color: colors.grey[300],
                fontWeight: 500
              }}
            >
              {start.toFormat('cccc')}
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: colors.primary[100],
                fontWeight: 600,
                mb: 1
              }}
            >
              {start.toFormat('HH:mm')} - {end.toFormat('HH:mm')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: colors.grey[300],
                fontSize: '0.9rem'
              }}
            >
              EST Timezone
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 3,
        pb: 12
      }}>
        {isSameDayOrPast ? (
          <Typography color="error" sx={{ mb: 2 }}>
            {timeError}
          </Typography>
        ) : (
          <Box sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            gap: 2,
            width: "100%",
            maxWidth: 400,
            justifyContent: "center"
          }}>
            <Button
              variant="contained"
              onClick={() => setOpenAttendModal(true)}
              sx={{
                width: "100%",
                minHeight: 56,
                fontSize: '1.1rem',
                fontWeight: 600,
                bgcolor: colors.greenAccent[500],
                color: colors.primary[500],
                borderRadius: 2,
                boxShadow: 3,
                textTransform: "none",
                '&:hover': {
                  bgcolor: colors.greenAccent[600],
                  boxShadow: 4,
                  transform: "translateY(-1px)",
                  transition: "all 0.2s ease-in-out"
                }
              }}
            >
              참석하기
            </Button>
            <Button
              variant="outlined"
              onClick={() => setOpenNotAttendModal(true)}
              sx={{
                width: "100%",
                minHeight: 56,
                fontSize: '1.1rem',
                fontWeight: 600,
                borderColor: colors.primary[100],
                color: colors.primary[100],
                borderRadius: 2,
                textTransform: "none",
                '&:hover': {
                  borderColor: colors.greenAccent[500],
                  transform: "translateY(-1px)",
                  transition: "all 0.2s ease-in-out"
                }
              }}
            >
              불참석
            </Button>
          </Box>
        )}
      </Box>
      {/* 참석하기 확인 모달 */}
      <Dialog
        open={openAttendModal}
        onClose={() => setOpenAttendModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: colors.primary[600] } }}
      >
        <DialogTitle sx={{
          textAlign: 'center',
          pt: 3,
          pb: 2,
          color: colors.primary[100],
          fontWeight: 600
        }}>
          참석하기
        </DialogTitle>
        <DialogContent sx={{ px: 4, pb: 3 }}>
          <Typography sx={{ mb: 2, color: colors.primary[100] }}>
            해당 일정에 참석하시겠습니까?
          </Typography>
          <Typography
            sx={{
              pl: 2,
              mb: 1,
              fontWeight: 600,
              color: colors.primary[100]
            }}
          >
            {start.toFormat('MMMM d, yyyy')} {start.toFormat('HH:mm')} - {end.toFormat('HH:mm')} (EST)
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button
            onClick={() => setOpenAttendModal(false)}
            variant="outlined"
            sx={{
              flex: 1,
              minHeight: 48,
              fontSize: '1rem',
              fontWeight: 500,
              borderColor: colors.primary[100],
              color: colors.primary[100],
              borderRadius: 2,
              textTransform: "none"
            }}
          >
            취소
          </Button>
          <Button
            onClick={handleAttend}
            variant="contained"
            sx={{
              flex: 1,
              minHeight: 48,
              fontSize: '1rem',
              fontWeight: 600,
              bgcolor: colors.greenAccent[500],
              color: colors.primary[500],
              borderRadius: 2,
              textTransform: "none",
              '&:hover': {
                bgcolor: colors.greenAccent[600]
              }
            }}
          >
            확인
          </Button>
        </DialogActions>
      </Dialog>
      {/* 불참석 확인 모달 */}
      <Dialog
        open={openNotAttendModal}
        onClose={() => setOpenNotAttendModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { bgcolor: colors.primary[600] } }}
      >
        <DialogTitle sx={{
          textAlign: 'center',
          pt: 3,
          pb: 2,
          color: colors.primary[100],
          fontWeight: 600
        }}>
          불참석
        </DialogTitle>
        <DialogContent sx={{ px: 4, pb: 3 }}>
          <Typography sx={{ mb: 2, color: colors.primary[100] }}>
            해당 일정에 불참석하시겠습니까?
          </Typography>
          <Typography
            sx={{
              pl: 2,
              mb: 1,
              fontWeight: 600,
              color: colors.primary[100]
            }}
          >
            {start.toFormat('MMMM d, yyyy')} {start.toFormat('HH:mm')} - {end.toFormat('HH:mm')} (EST)
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
          <Button
            onClick={() => setOpenNotAttendModal(false)}
            variant="outlined"
            sx={{
              flex: 1,
              minHeight: 48,
              fontSize: '1rem',
              fontWeight: 500,
              borderColor: colors.primary[100],
              color: colors.primary[100],
              borderRadius: 2,
              textTransform: "none"
            }}
          >
            취소
          </Button>
          <Button
            onClick={handleNotAttend}
            variant="contained"
            sx={{
              flex: 1,
              minHeight: 48,
              fontSize: '1rem',
              fontWeight: 600,
              bgcolor: colors.greenAccent[500],
              color: colors.primary[500],
              borderRadius: 2,
              textTransform: "none",
              '&:hover': {
                bgcolor: colors.greenAccent[600]
              }
            }}
          >
            확인
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default GroupParentForm;